/* globals gtag */
import React, { createContext, useContext, useState, useCallback } from 'react';

const StoreContext = createContext();

export const StoreProvider = ({ children }) => {
    const [currentPage, setCurrentPage] = useState('paintings');
    const [menuState, setMenuState] = useState(false);
    const [imageView, setImageView] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [screenType, setScreenType] = useState('desktop');
    const [paintings, setPaintingsState] = useState({});
    const [graphics, setGraphicsState] = useState({});
    const [activeAlbum, setActiveAlbumState] = useState(null);
    const [albumState, setAlbumState] = useState({ leftMost: 0 });

    const toggleMenu = useCallback(() => {
        setMenuState(prev => {
            const newState = !prev;
            if (newState && typeof gtag !== 'undefined') {
                gtag('event', 'menu open', {
                    event_category: 'menu'
                });
            }
            return newState;
        });
    }, []);

    const setPage = useCallback((pageName) => {
        setMenuState(false);
        setCurrentPage(pageName);
    }, []);

    const setPaintings = useCallback((list) => {
        setPaintingsState({
            leftMost: 0,
            list: list
        });
        setCurrentPage('paintings');
    }, []);

    const setGraphics = useCallback((list) => {
        setGraphicsState({
            leftMost: 0,
            list: list
        });
        setCurrentPage('graphics');
    }, []);

    const showImageView = useCallback((image) => {
        setImageView(true);
        setSelectedImage(image);
        if (typeof gtag !== 'undefined') {
            gtag('event', 'image preview', {
                event_category: 'image',
                event_label: image.title
            });
        }
    }, []);

    const hideImageView = useCallback(() => {
        setImageView(false);
        setSelectedImage(null);
    }, []);

    const setActiveAlbum = useCallback((album) => {
        setActiveAlbumState(album);
        setAlbumState({ leftMost: 0 });
        // Reset gallery pagination when opening/closing album to stay in sync with carousel scroll reset
        setPaintingsState(prev => ({ ...prev, leftMost: 0 }));
        setGraphicsState(prev => ({ ...prev, leftMost: 0 }));

        if (album && typeof gtag !== 'undefined') {
            gtag('event', 'album open', {
                event_category: 'album',
                event_label: album.title
            });
        }
    }, []);

    const changeScreenType = useCallback((type) => {
        setScreenType(type);
    }, []);

    const scrollLeft = useCallback(() => {
        const updateList = (prevList) => {
            if (prevList.leftMost > 0) {
                const step = screenType === 'mobile' ? 1 : 3;
                const nextImage = prevList.leftMost - step;
                const clampedNext = Math.max(0, nextImage);
                
                if (typeof gtag !== 'undefined') {
                    gtag('config', 'UA-162933668-1', { page_path: `/${currentPage}/${clampedNext}` });
                }
                return { ...prevList, leftMost: clampedNext };
            }
            return prevList;
        };

        if (activeAlbum) {
            setAlbumState(updateList);
        } else if (currentPage === 'paintings') {
            setPaintingsState(updateList);
        } else if (currentPage === 'graphics') {
            setGraphicsState(updateList);
        }
    }, [currentPage, screenType, activeAlbum]);

    const scrollRight = useCallback(() => {
        const updateList = (prevList) => {
            const listLength = activeAlbum ? activeAlbum.images.length : (prevList.list ? prevList.list.length : 0);
            const step = screenType === 'mobile' ? 1 : 3;
            const numPages = Math.ceil(listLength / step);
            const maxLeft = Math.max(0, (numPages - 1) * step);

            if (prevList.leftMost < maxLeft) {
                const nextImage = prevList.leftMost + step;
                const clampedNext = Math.min(nextImage, maxLeft);

                if (typeof gtag !== 'undefined') {
                    gtag('config', 'UA-162933668-1', { page_path: `/${currentPage}/${clampedNext}` });
                }
                return { ...prevList, leftMost: clampedNext };
            }
            return prevList;
        };

        if (activeAlbum) {
            setAlbumState(updateList);
        } else if (currentPage === 'paintings') {
            setPaintingsState(updateList);
        } else if (currentPage === 'graphics') {
            setGraphicsState(updateList);
        }
    }, [currentPage, screenType, activeAlbum]);

    const store = {
        currentPage,
        menuState,
        imageView,
        selectedImage,
        screenType,
        paintings,
        graphics,
        activeAlbum,
        albumState,
        toggleMenu,
        setPage,
        setPaintings,
        setGraphics,
        showImageView,
        hideImageView,
        setActiveAlbum,
        scrollLeft,
        scrollRight,
        changeScreenType
    };

    return (
        <StoreContext.Provider value={store}>
            {children}
        </StoreContext.Provider>
    );
};

export const useStore = () => useContext(StoreContext);
